import { ensureVisitorUuid, getMeta } from "./db";

// Lazy-import amazon-connect-chatjs only when a visitor actually starts a chat.
// The library is ~250 kB minified and attaches `window.connect` for use by the
// rest of this module. Without lazy-load it dominates the landing-page payload.
// The package ships a .d.ts that isn't a TS module (side-effect-only), so the
// dynamic import is intentionally cast to bypass module-shape checks.
let chatJsLoaded: Promise<void> | null = null;
function loadChatJs(): Promise<void> {
  if (!chatJsLoaded) {
    chatJsLoaded = (import("amazon-connect-chatjs" as unknown as string) as unknown as Promise<unknown>).then(() => undefined);
  }
  return chatJsLoaded;
}

declare global {
  interface Window {
    connect: {
      ChatSession: {
        setGlobalConfig: (config: { region?: string }) => void;
        create: (args: {
          chatDetails: {
            contactId: string;
            participantId: string;
            participantToken: string;
          };
          options: { region: string };
          type: "CUSTOMER";
        }) => ChatSession;
      };
    };
  }
}

// Subset of amazon-connect-chatjs ChatSession we use.
interface ChatSession {
  connect: () => Promise<unknown>;
  disconnectParticipant: () => Promise<unknown>;
  sendMessage: (args: {
    contentType: string;
    message: string;
  }) => Promise<unknown>;
  sendEvent: (args: { contentType: string }) => Promise<unknown>;
  onMessage: (cb: (event: ChatEvent) => void) => void;
  onTyping: (cb: (event: ChatEvent) => void) => void;
  onConnectionEstablished: (cb: () => void) => void;
  onEnded: (cb: () => void) => void;
  onParticipantIdle: (cb: (event: ChatEvent) => void) => void;
  onParticipantReturned: (cb: (event: ChatEvent) => void) => void;
}

interface ChatEvent {
  data: {
    AbsoluteTime?: string;
    Content?: string;
    ContentType?: string;
    DisplayName?: string;
    Id?: string;
    ParticipantId?: string;
    ParticipantRole?: "CUSTOMER" | "AGENT" | "SYSTEM";
    Type?: "MESSAGE" | "EVENT";
  };
}

export interface ChatMessage {
  id: string;
  role: "agent" | "customer" | "system";
  displayName?: string;
  text: string;
  ts: number;
}

export interface StartChatContext {
  nodeType: string;
  nodeId: string;
  nodeTitle?: string;
  nodeSummary?: string;
  contactName?: string;
  contactEmail?: string;
  journeySummary?: string;
}

export interface ChatHandle {
  session: ChatSession;
  contactId: string;
  participantId: string;
  disconnect: () => Promise<void>;
}

const START_CHAT_URL: string | undefined = (import.meta.env
  .VITE_START_CHAT_URL as string | undefined) ?? undefined;

// "Talk to me about this work" is off by default. Set
// VITE_TALK_TO_ME_ENABLED=true at build time to surface the affordance.
// Even when enabled, START_CHAT_URL must also be present.
const TALK_TO_ME_ENABLED =
  String(import.meta.env.VITE_TALK_TO_ME_ENABLED ?? "").toLowerCase() === "true";

export function chatConfigured(): boolean {
  return (
    TALK_TO_ME_ENABLED &&
    typeof START_CHAT_URL === "string" &&
    START_CHAT_URL.length > 0
  );
}

export interface ChatCallbacks {
  onMessage: (msg: ChatMessage) => void;
  onClose: () => void;
  onTyping?: (role: "agent" | "customer" | "system", displayName?: string) => void;
  onConnected?: () => void;
}

export async function startChat(
  ctx: StartChatContext,
  callbacks: ChatCallbacks,
): Promise<ChatHandle> {
  if (!START_CHAT_URL) throw new Error("Chat is not configured for this build.");

  await loadChatJs();
  const visitorUuid = await ensureVisitorUuid();
  const meta = await getMeta();
  const body = {
    visitorUuid,
    contactName: ctx.contactName ?? meta?.contactName,
    contactEmail: ctx.contactEmail ?? meta?.contactEmail,
    nodeType: ctx.nodeType,
    nodeId: ctx.nodeId,
    nodeTitle: ctx.nodeTitle,
    nodeSummary: ctx.nodeSummary,
    journeySummary: ctx.journeySummary,
  };

  const resp = await fetch(START_CHAT_URL, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`Failed to start chat (${resp.status}): ${text || resp.statusText}`);
  }
  const data = (await resp.json()) as {
    ok: boolean;
    contactId: string;
    participantId: string;
    participantToken: string;
    region: string;
  };

  window.connect.ChatSession.setGlobalConfig({ region: data.region });
  const session = window.connect.ChatSession.create({
    chatDetails: {
      contactId: data.contactId,
      participantId: data.participantId,
      participantToken: data.participantToken,
    },
    options: { region: data.region },
    type: "CUSTOMER",
  });

  session.onMessage((event) => {
    const msg = toMessage(event);
    if (msg) callbacks.onMessage(msg);
  });
  session.onTyping((event) => {
    if (!callbacks.onTyping) return;
    const d = event.data;
    const role: "agent" | "customer" | "system" =
      d.ParticipantRole === "AGENT"
        ? "agent"
        : d.ParticipantRole === "CUSTOMER"
          ? "customer"
          : "system";
    callbacks.onTyping(role, d.DisplayName);
  });
  session.onConnectionEstablished(() => callbacks.onConnected?.());
  session.onEnded(() => callbacks.onClose());

  await session.connect();

  return {
    session,
    contactId: data.contactId,
    participantId: data.participantId,
    async disconnect() {
      try {
        await session.disconnectParticipant();
      } catch {
        /* ignore */
      }
    },
  };
}

export async function sendChatMessage(handle: ChatHandle, text: string) {
  await handle.session.sendMessage({
    contentType: "text/plain",
    message: text,
  });
}

// amazon-connect-chatjs fires onMessage for both text MESSAGEs and lifecycle
// EVENTs (participant joined/left, typing-stopped, etc.). Rendering EVENTs
// produced the empty agent bubbles we saw. We only render real messages, and
// only text/plain or text/markdown content.
function toMessage(event: ChatEvent): ChatMessage | null {
  const d = event.data;
  if (d.Type !== "MESSAGE") return null;
  if (
    d.ContentType !== "text/plain" &&
    d.ContentType !== "text/markdown"
  )
    return null;
  if (!d.Id || !d.Content) return null;

  const role: ChatMessage["role"] =
    d.ParticipantRole === "AGENT"
      ? "agent"
      : d.ParticipantRole === "CUSTOMER"
        ? "customer"
        : "system";
  return {
    id: d.Id,
    role,
    displayName: d.DisplayName,
    text: d.Content,
    ts: d.AbsoluteTime ? Date.parse(d.AbsoluteTime) : Date.now(),
  };
}
